import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCcw, CheckCircle2, Loader2, Lock, Download, Truck } from "lucide-react";
import { API } from "../context/CartContext";

const STATUS_STYLES = {
  paid: "bg-green-100 text-green-800",
  awaiting_transfer: "bg-yellow-100 text-yellow-800",
  pending: "bg-orange-100 text-orange-800",
  shipped: "bg-blue-100 text-blue-800",
};

const GATEWAY_LABELS = { bank_transfer: "Transfer BCA", stripe: "Stripe (Kartu)", midtrans: "Midtrans" };

const fmtIDR = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("agrodyke_admin_token")}` });

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("agrodyke_admin_token", data.access_token);
      onLogin();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login gagal. Coba lagi.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center px-6">
      <form onSubmit={submit} className="bg-white rounded-3xl p-10 max-w-sm w-full border border-brand-green/15 shadow-xl">
        <div className="w-14 h-14 rounded-2xl bg-brand-yellow flex items-center justify-center text-brand-dark mb-5">
          <Lock size={24} />
        </div>
        <h1 className="font-heading text-4xl text-brand-dark uppercase">Admin Login</h1>
        <p className="text-sm text-brand-brown mt-2">Masuk untuk mengelola pesanan Agrodyke.</p>
        <div className="mt-6 space-y-4">
          <input data-testid="admin-email-input" type="email" required value={email} placeholder="Email admin"
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-5 py-3 rounded-2xl border border-brand-green/25 text-sm bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-orange" />
          <input data-testid="admin-password-input" type="password" required value={password} placeholder="Password"
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-5 py-3 rounded-2xl border border-brand-green/25 text-sm bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-orange" />
        </div>
        {error && <p data-testid="admin-login-error" className="mt-3 text-xs text-red-600 font-semibold">{error}</p>}
        <button data-testid="admin-login-button" type="submit" disabled={busy}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-orange text-white font-bold text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={18} className="animate-spin" /> : "Masuk"}
        </button>
      </form>
    </div>
  );
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(null);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [tab, setTab] = useState("orders");
  const [loading, setLoading] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: authHeader() });
      setOrders(data);
      const q = await axios.get(`${API}/admin/quotations`, { headers: authHeader() });
      setQuotes(q.data);
      setAuthed(true);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("agrodyke_admin_token");
        setAuthed(false);
      } else toast.error("Gagal memuat pesanan");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("agrodyke_admin_token")) loadOrders();
    else setAuthed(false);
  }, [loadOrders]);

  const markPaid = async (orderId) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/mark-paid`, {}, { headers: authHeader() });
      toast.success(`${orderId} ditandai lunas`);
      loadOrders();
    } catch { toast.error("Gagal menandai lunas"); }
  };

  const shipOrder = async (orderId) => {
    const resi = window.prompt("Masukkan nomor resi pengiriman:");
    if (!resi || !resi.trim()) return;
    try {
      await axios.put(`${API}/admin/orders/${orderId}/ship`, { tracking_number: resi.trim() }, { headers: authHeader() });
      toast.success(`${orderId} ditandai dikirim (resi: ${resi.trim()})`);
      loadOrders();
    } catch { toast.error("Gagal menandai dikirim"); }
  };

  const logout = () => {
    localStorage.removeItem("agrodyke_admin_token");
    setAuthed(false);
  };

  const exportCsv = () => {
    const rows = [["order_id", "tanggal", "nama", "email", "telepon", "alamat_pemesan", "alamat_pengiriman", "item", "metode", "mata_uang", "total_idr", "total_usd", "status"]];
    orders.forEach((o) => rows.push([
      o.order_id, o.created_at, o.customer?.name, o.customer?.email, o.customer?.phone || "",
      o.customer?.address || "", o.customer?.shipping_address || "",
      o.items?.map((it) => `${it.weight_kg}kg x${it.quantity}`).join(" | "),
      o.gateway, o.currency, o.total_idr, o.total_usd, o.status,
    ]));
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `pesanan-agrodyke-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (authed === null) {
    return <div className="min-h-screen bg-brand-cream flex items-center justify-center"><Loader2 size={40} className="animate-spin text-brand-orange" /></div>;
  }
  if (authed === false) return <AdminLogin onLogin={loadOrders} />;

  return (
    <div className="min-h-screen bg-brand-cream py-10 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-5xl text-brand-dark uppercase">Pesanan Masuk</h1>
            <p className="text-sm text-brand-brown font-semibold mt-1">{orders.length} pesanan terdaftar</p>
          </div>
          <div className="flex gap-3">
            <button data-testid="admin-export-csv-button" onClick={exportCsv}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-green text-white font-bold text-sm hover:bg-brand-dark transition-colors">
              <Download size={16} /> Unduh CSV
            </button>
            <button data-testid="admin-refresh-button" onClick={loadOrders} disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-brand-green/20 text-brand-dark font-bold text-sm hover:bg-brand-yellow transition-colors">
              <RefreshCcw size={16} className={loading ? "animate-spin" : ""} /> Muat Ulang
            </button>
            <button data-testid="admin-logout-button" onClick={logout}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-dark text-white font-bold text-sm hover:bg-brand-brown transition-colors">
              <LogOut size={16} /> Keluar
            </button>
          </div>
        </div>
        <div className="flex gap-3 mb-6">
          <button data-testid="admin-tab-orders" onClick={() => setTab("orders")}
            className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${tab === "orders" ? "bg-brand-orange text-white" : "bg-white text-brand-brown border border-brand-green/20"}`}>
            Pesanan ({orders.length})
          </button>
          <button data-testid="admin-tab-quotes" onClick={() => setTab("quotes")}
            className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${tab === "quotes" ? "bg-brand-orange text-white" : "bg-white text-brand-brown border border-brand-green/20"}`}>
            Quotation Ekspor ({quotes.length})
          </button>
        </div>
        {tab === "orders" && (
        <div className="overflow-x-auto rounded-3xl border border-brand-green/15 bg-white shadow-[0_8px_30px_rgba(109,76,65,0.08)]">
          <table className="w-full min-w-[860px]" data-testid="admin-orders-table">
            <thead>
              <tr className="bg-brand-dark text-brand-yellow text-start">
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Order ID</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Tanggal</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Pembeli</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Item</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Metode</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Total</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Status</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan="8" className="px-5 py-10 text-center text-sm text-brand-brown">Belum ada pesanan.</td></tr>
              )}
              {orders.map((o, i) => (
                <tr key={o.order_id} className={i % 2 === 0 ? "bg-white" : "bg-brand-cream"}>
                  <td className="px-5 py-4 text-xs font-mono font-bold text-brand-dark">{o.order_id}</td>
                  <td className="px-5 py-4 text-xs text-brand-brown">{new Date(o.created_at).toLocaleString("id-ID")}</td>
                  <td className="px-5 py-4 text-xs text-brand-brown">
                    <div className="font-bold text-brand-dark">{o.customer?.name}</div>
                    <div>{o.customer?.email}</div>
                  </td>
                  <td className="px-5 py-4 text-xs text-brand-brown">
                    {o.items?.map((it) => `${it.weight_kg}kg×${it.quantity}`).join(", ")}
                  </td>
                  <td className="px-5 py-4 text-xs font-semibold text-brand-brown">{GATEWAY_LABELS[o.gateway] || o.gateway}</td>
                  <td className="px-5 py-4 text-sm font-bold text-brand-dark">
                    {o.currency === "USD" ? `$${Number(o.total_usd).toFixed(2)}` : fmtIDR(o.total_idr)}
                  </td>
                  <td className="px-5 py-4">
                    <span data-testid={`order-status-${o.order_id}`}
                      className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase ${STATUS_STYLES[o.status] || "bg-gray-100 text-gray-600"}`}>
                      {o.status === "awaiting_transfer" ? "Menunggu Transfer" : o.status === "paid" ? "Lunas" : o.status === "shipped" ? "Dikirim" : o.status}
                    </span>
                    {o.tracking_number && (
                      <div className="text-[10px] font-mono text-brand-brown mt-1">Resi: {o.tracking_number}</div>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {o.status !== "paid" && o.status !== "shipped" && (
                      <button data-testid={`mark-paid-${o.order_id}`} onClick={() => markPaid(o.order_id)}
                        className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-brand-green text-white text-xs font-bold hover:bg-brand-dark transition-colors">
                        <CheckCircle2 size={14} /> Tandai Lunas
                      </button>
                    )}
                    {o.status === "paid" && (
                      <button data-testid={`ship-order-${o.order_id}`} onClick={() => shipOrder(o.order_id)}
                        className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-blue-600 text-white text-xs font-bold hover:bg-brand-dark transition-colors">
                        <Truck size={14} /> Kirim
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        {tab === "quotes" && (
        <div className="overflow-x-auto rounded-3xl border border-brand-green/15 bg-white shadow-[0_8px_30px_rgba(109,76,65,0.08)]">
          <table className="w-full min-w-[860px]" data-testid="admin-quotes-table">
            <thead>
              <tr className="bg-brand-dark text-brand-yellow">
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">ID</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Tanggal</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Nama</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Perusahaan</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Kontak</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Negara</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Jumlah</th>
                <th className="text-start px-5 py-4 font-heading uppercase tracking-wide">Pesan</th>
              </tr>
            </thead>
            <tbody>
              {quotes.length === 0 && (
                <tr><td colSpan="8" className="px-5 py-10 text-center text-sm text-brand-brown">Belum ada permintaan quotation.</td></tr>
              )}
              {quotes.map((q, i) => (
                <tr key={q.quotation_id} className={i % 2 === 0 ? "bg-white" : "bg-brand-cream"}>
                  <td className="px-5 py-4 text-xs font-mono font-bold text-brand-dark">{q.quotation_id}</td>
                  <td className="px-5 py-4 text-xs text-brand-brown">{new Date(q.created_at).toLocaleString("id-ID")}</td>
                  <td className="px-5 py-4 text-xs font-bold text-brand-dark">{q.name}</td>
                  <td className="px-5 py-4 text-xs text-brand-brown">{q.company || "-"}</td>
                  <td className="px-5 py-4 text-xs text-brand-brown">{q.email}<br />{q.phone || ""}</td>
                  <td className="px-5 py-4 text-xs font-semibold text-brand-brown">{q.country}</td>
                  <td className="px-5 py-4 text-sm font-bold text-brand-dark">{Number(q.quantity_kg).toLocaleString("id-ID")} kg</td>
                  <td className="px-5 py-4 text-xs text-brand-brown max-w-[240px]">{q.message || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}
